import apiClient from "./client";

export const fetchVideos = async ({
  page = 1,
  pageSize = 10,
  status = "",
  processed = "", // "", "true", "false"
  minMalfunctions = "",
  maxMalfunctions = "",
  startDate = "",
  endDate = "",
  unsolvedOnly = false,
  ordering = "-timestamp",
  search = "",
} = {}) => {
  try {
    const params = {
      page,
      page_size: pageSize,
    };

    // Backend filters
    if (status) params.status = status;
    if (processed !== "") params.processed = processed;

    if (minMalfunctions !== "") params.malfunction_count__gte = Number(minMalfunctions);
    if (maxMalfunctions !== "") params.malfunction_count__lte = Number(maxMalfunctions);

    if (startDate) params.timestamp__gte = startDate; // "YYYY-MM-DD"
    if (endDate) params.timestamp__lte = endDate;     // "YYYY-MM-DD"

    if (unsolvedOnly) params.unsolved_only = true;

    // Optional ordering/search
    if (ordering) params.ordering = ordering;
    if (search) params.search = search;

    const { data } = await apiClient.get("api/videos/", { params });

    // Handle both paginated and non-paginated responses
    if (Array.isArray(data)) {
      return { results: data, count: data.length, next: null, previous: null };
    }

    return {
      results: data.results ?? [],
      count: data.count ?? 0,
      next: data.next ?? null,
      previous: data.previous ?? null,
    };
  } catch (error) {
    console.error("Error fetching videos:", error);
    throw error;
  }
};

export const uploadVideo = async ({ video, route_file, onUploadProgress }) => {
  const form = new FormData();
  form.append("video", video);
  form.append("route_file", route_file);  // ← add route file

  const res = await apiClient.post("api/videos/", form, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (progressEvent) => {
      if (typeof onUploadProgress !== "function") return;

      // Axios in browsers may provide (loaded,total) and/or a normalized `progress` value.
      const loaded = progressEvent?.loaded;
      const total = progressEvent?.total;

      let percent = null;
      if (typeof progressEvent?.progress === "number") {
        percent = Math.round(progressEvent.progress * 100);
      } else if (typeof loaded === "number" && typeof total === "number" && total > 0) {
        percent = Math.round((loaded * 100) / total);
      }

      if (percent == null) return;
      onUploadProgress(Math.max(0, Math.min(100, percent)));
    },
    timeout: 0,
  });

  return res.data;
};

export const deleteVideo = async (id) => {
  await apiClient.delete(`api/videos/${id}/`);
  return id;
};

export const getVideoSignedUrl = async (id) => {
  const { data } = await apiClient.get(`api/videos/${id}/signed-url/`);
  return data.video_url;
};

export const exportVideo = async (videoId, format) => {
  try {
    const { data } = await apiClient.get(
      `api/videos/${videoId}/export/`,
      {
        params: { fmt: format },
        responseType: 'blob',
      }
    );
    

    // Create download link
    const url = window.URL.createObjectURL(data);
    const link = document.createElement("a");
    link.href = url;

    const filename = `video_${videoId}_report.${
      format === "geojson" ? "geojson" : "csv"
    }`;
    link.setAttribute("download", filename);

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error(`Export error (${format}):`, error);
    throw error;
  }
};

export const fetchVideoById = async (videoId) => {
  const { data } = await apiClient.get(`api/videos/${videoId}/`);
  return data;
};

export const getAnnotatedVideoUrl = async (videoId) => {
  try {
    const { data } = await apiClient.get(
      `api/videos/${videoId}/export-annotated/`,
      {
        responseType: 'blob',
        timeout: 600000, // ← ADD 10 minute timeout (video processing takes time)
        onDownloadProgress: (progressEvent) => {
          // Optional: log download progress
          if (progressEvent.loaded && progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            console.log(`Downloading annotated video: ${percentCompleted}%`);
          }
        }
      }
    );
    
    // Verify we got a video blob
    if (!(data instanceof Blob) || data.size === 0) {
      throw new Error('Invalid video data received');
    }
    
    console.log(`Received annotated video blob: ${(data.size / 1024 / 1024).toFixed(2)} MB`);
    
    // Create blob URL for video playback
    const url = window.URL.createObjectURL(data);
    return url;
  } catch (error) {
    console.error('Error loading annotated video:', error);
    
    // Better error messages
    if (error.code === 'ECONNABORTED') {
      throw new Error('Video processing timed out. The video might be too large.');
    } else if (error.response?.status === 500) {
      throw new Error('Server error while processing video. Check backend logs.');
    } else if (error.response?.status === 404) {
      throw new Error('No detections found for this video.');
    }
    
    throw error;
  }
};
